import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import TestimonialSlider from '../components/TestimonialSlider';
import { Search, CheckCircle } from 'lucide-react';

const TrademarkSearch = () => {
  const steps = [
    {
      title: 'Information Collection',
      description: 'We collect details about your proposed trademark including the mark itself, intended goods/services, and your industry to conduct a targeted and comprehensive search.',
    },
    {
      title: 'Research and Analysis',
      description: 'Our trademark specialists conduct thorough searches through USPTO databases, state registrations, common law sources, and online databases to identify potential conflicts and assess registrability.',
    },
    {
      title: 'Comprehensive Report',
      description: 'We provide a detailed report analyzing search results, identifying potential conflicts, assessing risk levels, and recommending next steps for your trademark protection strategy.',
    },
  ];

  const packages = [
    {
      name: 'FEDERAL & STATE SEARCH',
      price: '$149',
      priceNote: '',
      features: [
        'Trademark search',
        'The federal search provides any similar names, logos, or slogans that are registered or pending with the USPTO',
        'State Search',
        'The state search does the same for all 50 states',
        'Detailed online report',
        'Identifies any matches with all the information you need to strategize your next move',
      ],
    },
    {
      name: 'FEDERAL, STATE & COMMON LAW SEARCH',
      price: '$299',
      priceNote: '',
      features: [
        'Includes the information from the Federal, State & Common Law Search, and also includes:',
        'Corporate name search',
        'We run a search for all corporate and other business entity names in all 50 states.',
        'Corporate Directories',
        'We search the corporate directories for listing of others who might be using your name.',
      ],
      popular: true,
    },
    {
      name: 'GLOBAL SEARCH',
      price: '$499',
      priceNote: '',
      features: [
        'Global search',
        'We search for pending and registered marks in these multi-national jurisdictions:',
        'Domain names',
        'The European community',
        'Domain names',
        'World Intellectual Property Organization (WIPO)',
      ],
    },
  ];

  const usps = [
    {
      title: 'Thorough Research',
      description: 'Our trademark searches go beyond basic USPTO queries. We examine federal, state, and common law sources to identify potential conflicts that could affect your trademark application or business operations.',
    },
    {
      title: 'Expert Analysis',
      description: 'Every search is reviewed by experienced trademark attorneys who assess the likelihood of confusion, evaluate risk factors, and provide strategic recommendations for protecting your brand.',
    },
    {
      title: 'Fast Turnaround',
      description: 'We understand time is critical. Most searches are completed within 3-5 business days, with rush options available for urgent needs. You receive a comprehensive report promptly.',
    },
    {
      title: 'Strategic Guidance',
      description: 'Beyond identifying conflicts, we provide actionable advice on how to proceed, whether that means filing your application, modifying your mark, or developing alternative protection strategies.',
    },
  ];

  const testimonials = [
    {
      name: 'Amanda Foster',
      company: 'Startup Ventures',
      text: 'The comprehensive search saved us from a costly mistake. They identified a similar mark we hadn\'t found and helped us choose a stronger alternative.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop',
    },
    {
      name: 'Michael Torres',
      company: 'Retail Solutions',
      text: 'Detailed report with clear recommendations. The attorney review gave us confidence to move forward with our trademark application.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop',
    },
    {
      name: 'Jennifer Kim',
      company: 'Tech Innovators',
      text: 'Fast and thorough service. Their search revealed potential issues we could address before filing, saving time and money.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop',
    },
    {
      name: 'Carlos Rivera',
      company: 'Design Studio',
      text: 'The premium search was incredibly detailed, covering everything from federal databases to social media. Worth every penny.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=200&h=200&fit=crop',
    },
    {
      name: 'Laura Peterson',
      company: 'Food & Beverage Co.',
      text: 'Professional service with excellent communication. They explained the search results clearly and provided practical advice.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=200&h=200&fit=crop',
    },
  ];

  const faqs = [
    {
      question: 'Why do I need a trademark search?',
      answer: 'A trademark search helps identify existing marks that could conflict with yours, reducing the risk of rejection by the USPTO, infringement claims, and costly rebranding. It\'s essential due diligence before investing in brand development and filing applications.',
    },
    {
      question: 'What does a trademark search include?',
      answer: 'Our searches examine USPTO federal registrations, state trademark databases, common law uses, domain names, and business directories. Comprehensive searches also include social media platforms and industry-specific databases.',
    },
    {
      question: 'How long does a trademark search take?',
      answer: 'Standard searches are completed within 3-5 business days. Basic searches may be ready in 1-2 days, while premium searches with international components may take up to 7 days. Rush processing is available.',
    },
    {
      question: 'What if the search finds a conflict?',
      answer: 'If we identify potential conflicts, our report includes a risk assessment and recommendations. Options may include modifying your mark, proceeding with calculated risk, choosing a different mark, or seeking a coexistence agreement.',
    },
    {
      question: 'Can I file my trademark without a search?',
      answer: 'While technically possible, it\'s not recommended. Filing without a search risks rejection, wasted filing fees, and potential infringement issues. A thorough search is a small investment that can save significant costs and complications.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1E1E1E] via-[#2a2a2a] to-[#1E1E1E] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="page-title text-white mb-6 animate-in fade-in slide-in-from-top duration-700">
              Comprehensive Trademark Search
            </h1>
            <p className="body-large text-gray-300 mb-8 animate-in fade-in slide-in-from-bottom duration-700">
              Avoid costly conflicts and rejections with professional trademark searches. Ensure your mark is available before investing in your brand.
            </p>
            <Link to="/contact-us">
              <Button size="lg" className="bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E] font-semibold px-12 text-lg h-14 transition-all duration-300 hover:scale-105">
                Start Your Trademark Search
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Service */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="section-heading">About Trademark Search</h2>
              <p className="body-regular">
                A comprehensive trademark search is a crucial first step before filing your trademark application or launching your brand. This detailed investigation examines existing trademarks to identify potential conflicts that could lead to application rejection, infringement claims, or costly legal disputes.
              </p>
              <p className="body-regular">
                Our professional trademark search services go far beyond simple USPTO database queries. We conduct multi-jurisdictional searches covering federal registrations, state trademarks, common law uses, domain names, and industry-specific sources to provide you with a complete picture of potential obstacles and the strength of your proposed mark.
              </p>
            </div>
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1589578527966-fdac0f44566c?w=800"
                alt="Trademark Search"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-6">Our Search Process</h2>
            <p className="body-large max-w-3xl mx-auto">
              We follow a systematic approach to ensure comprehensive trademark clearance for your brand.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 rounded-full bg-[#E5B80B] text-[#1E1E1E] flex items-center justify-center text-3xl font-bold mb-6 shadow-lg">
                    {index + 1}
                  </div>
                  <h3 className="sub-heading mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-1/2 w-full h-0.5 bg-[#E5B80B]/30"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-6">Trademark Search Packages</h2>
            <p className="body-large max-w-3xl mx-auto">
              Select the search level that matches your needs. All searches include expert analysis and detailed reporting.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <Card key={index} className={`relative ${pkg.popular ? 'border-4 border-[#E5B80B] shadow-2xl scale-105' : 'border-2'}`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-[#E5B80B] text-[#1E1E1E] text-sm px-4 py-1">Most Popular</Badge>
                  </div>
                )}
                <CardContent className="pricing-card p-6">
                  <div className="pricing-header">
                    <h3 className="card-title text-white mb-2">{pkg.name}</h3>
                    <div className="border-t border-white/30 w-12 mx-auto my-2"></div>
                    <span className="text-4xl font-bold">{pkg.price}</span>
                  </div>
                  <div className="pricing-features mt-6">
                    <ul className="space-y-3">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-[#E5B80B] mr-2 mt-0.5 flex-shrink-0" />
                          <span className="body-small text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-xs text-gray-500 mb-4 text-center mt-4">{pkg.priceNote}</p>
                  <Link to="/contact-us">
                    <Button className={`w-full h-12 font-semibold transition-all duration-300 mt-auto ${
                      pkg.popular
                        ? 'bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E]'
                        : 'bg-[#1E1E1E] hover:bg-[#2a2a2a] text-white'
                    }`}>
                      Get Started
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-6">Why Choose Our Search Services</h2>
            <div className="max-w-3xl mx-auto space-y-6">
              <p className="body-regular">
                Trademark searches require specialized knowledge of search techniques, legal standards, and trademark office procedures. Our team combines advanced search technology with attorney expertise to deliver comprehensive results you can rely on.
              </p>
              <p className="body-regular">
                We don't just provide raw search data. Every search includes professional analysis from experienced trademark attorneys who evaluate the results, assess risk factors, and provide strategic recommendations tailored to your specific business and brand protection goals.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {usps.map((usp, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <h3 className="sub-heading mb-4">{usp.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{usp.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#E5B80B] to-[#c99f09]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="section-heading mb-6">Protect Your Investment with a Professional Search</h2>
          <p className="text-xl text-[#1E1E1E]/90 mb-8 leading-relaxed">
            Don't risk rejection or infringement. Get a comprehensive trademark search before filing your application.
          </p>
          <Link to="/contact-us">
            <Button size="lg" className="bg-[#1E1E1E] hover:bg-[#2a2a2a] text-white font-semibold px-12 text-lg h-14 transition-all duration-300 hover:scale-105">
              Order Your Search Today
            </Button>
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="section-heading mb-4">Client Success Stories</h2>
          </div>
          <TestimonialSlider testimonials={testimonials} />
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white border border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-left text-lg font-semibold text-[#1E1E1E] hover:text-[#E5B80B]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default TrademarkSearch;
